/**
 * Customer-side onboarding tab components.
 * Contains: InvitationsTab, WorkflowsTab, DatasetsTab, PendingReviewsTab
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ActivateSupplierDialog from './ActivateSupplierDialog';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  UserPlus,
  FileText,
  CheckCircle2,
  MoreHorizontal,
  Send,
  Eye,
  Plus,
  ClipboardList,
  Building2,
  RefreshCw,
  ArrowRight,
  KeyRound,
  Link,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { OnboardingWorkflow, OnboardingDataset, SupplierQualification, onboardingAPI } from '../../lib/api';
import { toast } from 'sonner';
import { statusColors } from './onboardingConstants';
// CR-5965: the pure stage selectors live in a React-free module so the
// statistic tiles can reuse them without importing this component module.
import {
  groupPendingDueDiligenceSuppliers,
  deriveOrphanWorkflows,
} from './onboardingSelectors';
import type { SupplierGroup, FilledDatasetWithDetails, InvitedStageInvitation } from './onboardingSelectors';
import { formatDistanceToNow, format } from 'date-fns';

// --- Invitations Tab ---
interface InvitationsTabProps {
  // CR-5965: rows carry the synthetic 'wip' display status for suppliers who
  // accepted but have not yet submitted questionnaires, so the prop is typed
  // as the view model rather than the raw API status union.
  filteredInvitations: InvitedStageInvitation[];
  onSendInvitation: (id: number) => void;
  onResendInvitation: (id: number) => void;
  /** Manual stage advance for invitations the supplier can't accept themselves. */
  onAdvanceToDueDiligence: (id: number) => void;
  /** Hides the advance action for roles that may not approve suppliers. */
  canApproveSuppliers: boolean;
  /** Invitation id currently being advanced, so its row can show progress. */
  advancingInvitationId: number | null;
  /** Refetch after a supplier is activated with customer-set credentials. */
  onActivated: () => void;
}

export function InvitationsTab({
  filteredInvitations,
  onSendInvitation,
  onResendInvitation,
  onAdvanceToDueDiligence,
  canApproveSuppliers,
  advancingInvitationId,
  onActivated,
}: InvitationsTabProps) {
  const navigate = useNavigate();
  const [activating, setActivating] = useState<InvitedStageInvitation | null>(null);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle>Supplier Invitations</CardTitle>
          <CardDescription>Track and manage supplier onboarding invitations</CardDescription>
        </div>
        <Button onClick={() => navigate('/onboarding/invitations/new')}>
          <Plus className="h-4 w-4 mr-2" />New Invitation
        </Button>
      </CardHeader>
      <CardContent>
        {filteredInvitations.length === 0 ? (
          <div className="text-center py-8">
            <UserPlus className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">No invitations yet</h3>
            <p className="text-muted-foreground">Use the "New Invitation" button above to invite a supplier.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Sent</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="w-[70px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredInvitations.map((invitation) => (
                <TableRow key={invitation.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{invitation.invitee_company_name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{invitation.invitee_contact_name}</div>
                      <div className="text-sm text-muted-foreground">{invitation.invitee_email}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={statusColors[invitation.status] || statusColors['pending']}>
                      {invitation.status?.replace(/_/g, ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {invitation.sent_at ? (
                      <span className="text-sm">
                        {formatDistanceToNow(new Date(invitation.sent_at), { addSuffix: true })}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Not sent</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {invitation.expires_at ? (
                      <span className="text-sm">{format(new Date(invitation.expires_at), 'MMM d, yyyy')}</span>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {invitation.supplier && (
                          <DropdownMenuItem onClick={() => navigate(`/suppliers/wizard/${invitation.supplier}?step=1`)}>
                            <Eye className="h-4 w-4 mr-2" />View Supplier Details
                          </DropdownMenuItem>
                        )}
                        {invitation.status === 'pending' && (
                          <DropdownMenuItem onClick={() => onSendInvitation(invitation.id)}>
                            <Send className="h-4 w-4 mr-2" />Send Invitation
                          </DropdownMenuItem>
                        )}
                        {(invitation.status === 'sent' || invitation.status === 'opened' || invitation.status === 'expired') && (
                          <DropdownMenuItem onClick={() => onResendInvitation(invitation.id)}>
                            <RefreshCw className="h-4 w-4 mr-2" />Resend Invitation
                          </DropdownMenuItem>
                        )}
                        {/* Escape hatch for an invitation the supplier cannot
                            act on — typically the email never arrived, so
                            resending achieves nothing. Moves them into due
                            diligence by hand; pair with a temporary password
                            from User Management to get them signed in. */}
                        {canApproveSuppliers && invitation.supplier && (
                          <DropdownMenuItem
                            disabled={advancingInvitationId === invitation.id}
                            onClick={() => onAdvanceToDueDiligence(invitation.id)}
                          >
                            <ArrowRight className="h-4 w-4 mr-2" />
                            {advancingInvitationId === invitation.id ? 'Moving…' : 'Move to Due Diligence'}
                          </DropdownMenuItem>
                        )}
                        {/* The other way past a spam-filtered invitation:
                            rather than advancing the stage and leaving the
                            supplier with no way in, set their password here and
                            do both at once. */}
                        {canApproveSuppliers && invitation.supplier
                          && invitation.status !== 'accepted'
                          && invitation.status !== 'declined'
                          && invitation.status !== 'draft'
                          && invitation.status !== 'pending_approval' && (
                          <DropdownMenuItem onClick={() => setActivating(invitation)}>
                            <KeyRound className="h-4 w-4 mr-2" />Set Credentials &amp; Activate
                          </DropdownMenuItem>
                        )}
                        {invitation.invitation_token && (
                          <DropdownMenuItem onClick={() => {
                            const link = `${window.location.origin}/onboarding/accept/${invitation.invitation_token}`;
                            navigator.clipboard.writeText(link);
                            import('sonner').then(({ toast }) => toast.success('Invite link copied to clipboard'));
                          }}>
                            <Link className="h-4 w-4 mr-2" />Copy Invite Link
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
      <ActivateSupplierDialog
        invitation={activating}
        onClose={() => setActivating(null)}
        onActivated={onActivated}
      />
    </Card>
  );
}

// --- Workflows Tab ---
interface WorkflowsTabProps {
  filteredWorkflows: OnboardingWorkflow[];
}

export function WorkflowsTab({ filteredWorkflows }: WorkflowsTabProps) {
  const navigate = useNavigate();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Onboarding Workflows</CardTitle>
        <CardDescription>Track supplier progress through onboarding steps</CardDescription>
      </CardHeader>
      <CardContent>
        {filteredWorkflows.length === 0 ? (
          <div className="text-center py-8">
            <ClipboardList className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">No active onboardings</h3>
            <p className="text-muted-foreground">Onboardings will appear here when suppliers accept invitations</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Supplier</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead>Risk Score</TableHead>
                <TableHead>Started</TableHead>
                <TableHead className="w-[70px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredWorkflows.map((workflow) => (
                <TableRow key={workflow.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{workflow.supplier_name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={statusColors[workflow.status] || statusColors['pending']}>
                      {workflow.status?.replace(/_/g, ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Progress value={workflow.progress_percentage || 0} className="w-24" />
                      <span className="text-sm text-muted-foreground">{Math.round(workflow.progress_percentage || 0)}%</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {workflow.calculated_risk_score !== null && workflow.calculated_risk_score !== undefined ? (
                      <Badge variant={
                        workflow.calculated_risk_score > 70 ? 'destructive' :
                        workflow.calculated_risk_score > 40 ? 'secondary' : 'default'
                      }>
                        {workflow.calculated_risk_score}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {workflow.started_at ? (
                      <span className="text-sm">{formatDistanceToNow(new Date(workflow.started_at), { addSuffix: true })}</span>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {workflow.supplier && (
                          <DropdownMenuItem onClick={() => navigate(`/suppliers/wizard/${workflow.supplier}?step=1`)}>
                            <Eye className="h-4 w-4 mr-2" />View Supplier Details
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// --- Datasets Tab ---
interface DatasetsTabProps {
  datasets: OnboardingDataset[];
}

export function DatasetsTab({ datasets }: DatasetsTabProps) {
  const navigate = useNavigate();

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Questionnaire Templates</CardTitle>
          <CardDescription>Manage onboarding questionnaires and compliance forms</CardDescription>
        </div>
        <Button onClick={() => navigate('/onboarding/datasets/new')}>
          <Plus className="h-4 w-4 mr-2" />Create Template
        </Button>
      </CardHeader>
      <CardContent>
        {datasets.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">No Questionnaire Templates</h3>
            <p className="text-muted-foreground mb-4">Create questionnaire templates for supplier onboarding</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Questions</TableHead>
                <TableHead>Pass Score</TableHead>
                <TableHead>Mandatory</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {datasets.map((dataset) => (
                <TableRow key={dataset.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{dataset.name}</div>
                      {dataset.description && (
                        <div className="text-sm text-muted-foreground truncate max-w-[300px]">{dataset.description}</div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">{dataset.dataset_type.replace(/_/g, ' ')}</Badge>
                  </TableCell>
                  <TableCell>{dataset.questions_count ?? '-'}</TableCell>
                  <TableCell>{dataset.pass_score}%</TableCell>
                  <TableCell>
                    {dataset.is_mandatory ? (
                      <Badge className="bg-blue-100 text-blue-800">Required</Badge>
                    ) : (
                      <span className="text-muted-foreground">Optional</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {dataset.is_active ? (
                      <Badge className="bg-green-100 text-green-800">Active</Badge>
                    ) : (
                      <Badge variant="secondary">Inactive</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button variant="outline" size="sm" onClick={() => navigate(`/onboarding/datasets/${dataset.id}`)}>
                      <Eye className="h-4 w-4 mr-1" />View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// --- Pending Reviews Tab ---
interface PendingReviewsTabProps {
  pendingReviews: FilledDatasetWithDetails[];
  // CR-5984: workflows where the supplier accepted but no FilledDataset
  // rows exist (customer hadn't authored onboarding questionnaires
  // before approving). They render as a supplier row with an
  // "Awaiting Questionnaire Assignment" status so the supplier doesn't
  // disappear from the customer's view.
  orphanWorkflows?: OnboardingWorkflow[];
  // CR-5984 Path B: SupplierQualification rows for the same suppliers, used
  // by tenants whose questionnaires live in the legacy intelligence model
  // rather than FilledDataset. Rendered inside the same supplier card with
  // a click-through to /intelligence/qualifications/:id.
  submittedQualifications?: SupplierQualification[];
  /** Approved questionnaires for the same suppliers, rendered read-only.
   *  Deliberately separate from `submittedQualifications`: those drive the
   *  outstanding counts and the Ready-to-Approve state, so an approved row must
   *  never join them. Without this the reviewer had no way back to what they
   *  had just approved — the card emptied and "View Full Profile" only opens
   *  the initial-profile wizard, which holds no questionnaire answers. */
  settledQualifications?: SupplierQualification[];
  onAction?: () => void;
  // CR-4141: false for roles that cannot approve suppliers (e.g. Buyer). The
  // finalise action is hidden rather than disabled — a greyed-out button reads
  // as "not ready yet" when the real reason is "not permitted".
  canApproveSuppliers?: boolean;
  /** Questionnaires assignable to a supplier stuck without any. */
  assignableDatasets?: { id: number; name: string; question_count?: number }[];
  /** CR-6513: PDD-stage workflows used only to fill in assigned / in-progress
   *  counts on cards that already exist. See groupPendingDueDiligenceSuppliers. */
  countWorkflows?: OnboardingWorkflow[];
}

// CR-5965: the pure stage selectors moved to onboardingSelectors.ts so the
// statistic tiles can reuse them without importing this React module.
// Re-exported here so existing importers are unaffected.
export { groupPendingDueDiligenceSuppliers, deriveOrphanWorkflows };
export type { SupplierGroup };

/** A submission nobody has scored yet is NOT a failure.
 *
 * The old line read `Score: {score || 0}% · Failed` for a questionnaire the
 * supplier had only just sent in: `passed` stays false until a reviewer marks
 * it and `score_percentage` is null until someone awards points, so a supplier
 * who answered everything correctly was branded "Score: 0% · Failed" on the
 * very row that also said "Awaiting Review". Only state a verdict once a score
 * actually exists.
 */
/** Statuses that mean "a human has not judged this yet".
 *  The backend returns score_percentage 0 and passed=false for these, which is
 *  an absence of a verdict, not a failing one. */
const UNREVIEWED_STATUSES = new Set([
  'submitted', 'under_review', 'more_info_required', 'pending', 'in_progress',
]);

function scoreLabel(
  scorePercentage: number | null | undefined,
  passed: boolean | null | undefined,
  status?: string | null,
): string {
  if (status && UNREVIEWED_STATUSES.has(status)) return 'Not yet scored';
  const scored = scorePercentage !== null && scorePercentage !== undefined;
  if (!scored) return 'Not yet scored';
  const verdict = passed === true ? ' · Passed' : passed === false ? ' · Failed' : '';
  return `Score: ${Math.round(Number(scorePercentage))}%${verdict}`;
}

export function PendingReviewsTab({ pendingReviews, orphanWorkflows, submittedQualifications, settledQualifications = [], onAction, canApproveSuppliers = true, assignableDatasets = [], countWorkflows = [] }: PendingReviewsTabProps) {
  const navigate = useNavigate();
  const [expandedSupplier, setExpandedSupplier] = useState<string | null>(null);
  const [approvingWorkflow, setApprovingWorkflow] = useState<number | null>(null);
  // Questionnaire assignment for a supplier who arrived here with none —
  // previously a terminal state, since questionnaires bound only at approval.
  const [assignForWorkflow, setAssignForWorkflow] = useState<number | null>(null);
  const [assignPicks, setAssignPicks] = useState<number[]>([]);
  const [assigning, setAssigning] = useState(false);

  const submitAssignment = async () => {
    if (!assignForWorkflow || assignPicks.length === 0) return;
    setAssigning(true);
    try {
      const res = await onboardingAPI.assignQuestionnaires(assignForWorkflow, assignPicks);
      toast.success(`${res.data.questionnaires_assigned} questionnaire(s) assigned — the supplier can now respond.`);
      setAssignForWorkflow(null);
      setAssignPicks([]);
      onAction?.();
    } catch (err: unknown) {
      console.error('Failed to assign questionnaires:', err);
      const detail = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(detail || 'Failed to assign questionnaires');
    } finally {
      setAssigning(false);
    }
  };

  // CR-5953 rework 2: group via the shared helper so the rendered cards and
  // the tab badge count come from the same computation.
  const suppliers = groupPendingDueDiligenceSuppliers(
    pendingReviews, submittedQualifications ?? [], orphanWorkflows ?? [],
    countWorkflows ?? [],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pending Due Diligence</CardTitle>
        <CardDescription>
          {/* CR-4799: now covers submitted, under-review, more-info-requested and
              rejected questionnaires so customers can track every outstanding
              action from one place instead of losing rejected items. */}
          Questionnaires the supplier has sent you and any you have rejected or sent back for more information. Review each one, then approve the supplier.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {suppliers.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-4" />
            <h3 className="text-lg font-medium">All caught up!</h3>
            <p className="text-muted-foreground">No questionnaires pending review at this time</p>
          </div>
        ) : (
          <div className="space-y-4">
            {suppliers.map((supplier) => {
              const isExpanded = expandedSupplier === supplier.name;
              // CR-5984: an "orphan" supplier accepted the invitation but
              // never had FilledDatasets assigned, so reviews is empty.
              // Distinct badge + subtitle so the customer immediately sees
              // they need to wire up questionnaires.
              // CR-5984 Path B: orphan now requires BOTH FilledDataset reviews
              // and SupplierQualifications to be empty. A supplier with any SQ
              // is treated as questionnaire-driven for badge purposes.
              // CR-7046: "orphan" must mean NOTHING WAS ASSIGNED. A supplier
              // holding assigned-but-unsubmitted questionnaires is a different
              // state and previously rendered as orphan (or not at all).
              const assignedCount = supplier.assignedCount ?? 0;
              const settledCount = supplier.settledCount ?? 0;
              const submittedCount = supplier.reviews.length + supplier.qualifications.length;
              // Approved questionnaires for this supplier. Match on supplierId
              // only — the groups are keyed by name, but a settled row must not
              // attach to a same-named supplier at another company.
              const settledForSupplier = supplier.supplierId != null
                ? settledQualifications.filter(q => q.supplier === supplier.supplierId)
                : [];
              // "Nothing outstanding" is two different states. A supplier who
              // was never given a questionnaire needs one assigned; a supplier
              // whose every questionnaire has been approved needs approving.
              // Telling the second they have nothing assigned -- to the very
              // reviewer who just approved it -- is how this read before.
              const isSettled = submittedCount === 0 && assignedCount === 0 && settledCount > 0;
              const isOrphan = submittedCount === 0 && assignedCount === 0
                && settledCount === 0 && supplier.orphanWorkflowId != null;
              const isAwaitingSubmission = submittedCount === 0 && assignedCount > 0;
              // CR-6513: the ticket asks for "in progress" as its own step in the
              // progression. Without it a supplier part-way through their
              // questionnaires reads identically to one who has not opened them.
              const inProgressCount = supplier.inProgressCount ?? 0;
              const isInProgress = isAwaitingSubmission && inProgressCount > 0;
              const outstandingCount = Math.max(assignedCount - submittedCount, 0);
              const scoredReviews = supplier.reviews.filter(r => r.score_percentage != null && !isNaN(Number(r.score_percentage)));
              const scoredQuals = supplier.qualifications.filter(q => q.score_percentage != null && !isNaN(Number(q.score_percentage)));
              const totalScore = scoredReviews.reduce((sum, r) => sum + Number(r.score_percentage || 0), 0)
                + scoredQuals.reduce((sum, q) => sum + Number(q.score_percentage || 0), 0);
              const scoredCount = scoredReviews.length + scoredQuals.length;
              const avgScore = scoredCount > 0 ? Math.round(totalScore / scoredCount) : null;
              const totalQuestionnaires = supplier.reviews.length + supplier.qualifications.length;
              // Badge reflects the aggregate questionnaire state the customer
              // actually needs to act on:
              //  - "Awaiting Questionnaire Assignment" when no datasets exist (CR-5984)
              //  - "Awaiting Review" while any questionnaire is still submitted/under_review
              //  - "Approved" once the customer has signed off on each
              //  - "Needs Attention" when anything explicitly failed
              const allApprovedBadge = !isOrphan && totalQuestionnaires > 0
                && supplier.reviews.every(r => r.status === 'approved')
                && supplier.qualifications.every(q => q.status === 'approved');
              const anyExplicitlyFailed = supplier.reviews.some(r => r.passed === false || r.status === 'rejected')
                || supplier.qualifications.some(q => q.passed === false || q.status === 'rejected');
              const badgeLabel = isOrphan
                ? 'Awaiting Questionnaire Assignment'
                : isSettled
                  ? 'Ready to Approve Supplier'
                : isInProgress
                  ? `In Progress — ${inProgressCount} of ${assignedCount} questionnaire${assignedCount !== 1 ? 's' : ''}`
                : isAwaitingSubmission
                  ? `Awaiting Submission — ${assignedCount} questionnaire${assignedCount !== 1 ? 's' : ''}`
                  : anyExplicitlyFailed
                    ? 'Needs Attention'
                    : allApprovedBadge ? 'Approved' : 'Awaiting Review';
              const badgeClass = isOrphan
                ? 'bg-amber-100 text-amber-800'
                : isSettled
                  ? 'bg-green-600 text-white'
                : isInProgress
                  ? 'bg-yellow-100 text-yellow-800'
                : isAwaitingSubmission
                  ? 'bg-slate-100 text-slate-800'
                  : anyExplicitlyFailed
                    ? 'bg-red-100 text-red-800'
                    : allApprovedBadge ? 'bg-green-600 text-white' : 'bg-blue-100 text-blue-800';
              const latestFromReviews = supplier.reviews.reduce((latest, r) => {
                if (!r.submitted_at) return latest;
                return !latest || new Date(r.submitted_at) > new Date(latest) ? r.submitted_at : latest;
              }, '' as string);
              const latestSubmit = supplier.qualifications.reduce((latest, q) => {
                if (!q.submitted_at) return latest;
                return !latest || new Date(q.submitted_at) > new Date(latest) ? q.submitted_at : latest;
              }, latestFromReviews);

              return (
                <div key={supplier.name} className="border rounded-lg overflow-hidden">
                  {/* Supplier header - click to expand */}
                  <div
                    className="flex items-center gap-4 p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                    onClick={() => setExpandedSupplier(isExpanded ? null : supplier.name)}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                        <Building2 className="h-5 w-5 text-blue-700" />
                      </div>
                      <div>
                        <p className="font-semibold">{supplier.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {isOrphan
                            ? 'Supplier accepted the invitation — no questionnaires have been assigned to review yet.'
                            : isSettled
                              ? `All ${settledCount} questionnaire${settledCount !== 1 ? 's have' : ' has'} been reviewed — approve the supplier to finish onboarding.`
                            : isInProgress
                              ? `Supplier is completing ${inProgressCount} of ${assignedCount} assigned questionnaire${assignedCount !== 1 ? 's' : ''} — not yet submitted.`
                            : isAwaitingSubmission
                              ? `${assignedCount} onboarding questionnaire${assignedCount !== 1 ? 's' : ''} assigned — awaiting submission from the supplier.`
                              : <>
                                  {totalQuestionnaires} questionnaire{totalQuestionnaires !== 1 ? 's' : ''} submitted
                                  {outstandingCount > 0 && ` · ${outstandingCount} outstanding`}
                                  {latestSubmit && ` · ${formatDistanceToNow(new Date(latestSubmit), { addSuffix: true })}`}
                                </>}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {avgScore !== null && (
                        <Badge className={avgScore >= 70 ? 'bg-green-100 text-green-800' : avgScore >= 40 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}>
                          Avg Score: {avgScore}%
                        </Badge>
                      )}
                      <Badge variant="outline" className={badgeClass}>
                        {badgeLabel}
                      </Badge>
                      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </div>
                  </div>

                  {/* Expanded: questionnaire details + actions */}
                  {isExpanded && (
                    <div className="border-t bg-muted/10 p-4 space-y-3">
                      {isAwaitingSubmission && (
                        <div className="p-3 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-800">
                          {assignedCount} onboarding questionnaire{assignedCount !== 1 ? 's have' : ' has'} been
                          assigned and {assignedCount !== 1 ? 'are' : 'is'} visible to the supplier in My Onboarding.
                          {/* CR-6513: say whether they have actually started, so
                              the customer can tell a stalled supplier from a
                              working one without leaving the page. */}
                          {isInProgress
                            ? ` The supplier has started ${inProgressCount} of ${assignedCount} — nothing to review until they submit.`
                            : ' Awaiting submission — there is nothing to review yet.'}
                        </div>
                      )}
                      {isOrphan && (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-900">
                          No onboarding questionnaires were assigned when this supplier was approved.
                          Set up an onboarding dataset on the
                          {' '}
                          <button
                            type="button"
                            className="underline font-medium"
                            onClick={(e) => { e.stopPropagation(); navigate('/onboarding/datasets'); }}
                          >
                            Onboarding Datasets
                          </button>
                          {' '}
                          page, then re-invite this supplier to gather their responses.
                        </div>
                      )}
                      {supplier.reviews.map((review) => {
                        // CR-4799: surface the specific state the customer is
                        // looking at. Rejected/more_info_required rows were
                        // previously invisible; now they sit here with a
                        // colour-coded badge and an actionable label.
                        const statusLabelMap: Record<string, { label: string; className: string }> = {
                          submitted: { label: 'Awaiting Review', className: 'bg-blue-100 text-blue-800' },
                          under_review: { label: 'Under Review', className: 'bg-purple-100 text-purple-800' },
                          more_info_required: { label: 'More Info Requested', className: 'bg-amber-100 text-amber-800' },
                          rejected: { label: 'Rejected — Awaiting Resubmit', className: 'bg-red-100 text-red-800' },
                          approved: { label: 'Approved', className: 'bg-green-100 text-green-800' },
                        };
                        const statusBadge = review.status ? statusLabelMap[review.status] : undefined;
                        return (
                        <div key={review.id} className="flex items-center gap-4 p-3 bg-white rounded-md border">
                          <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm font-medium">{review.dataset_name || 'Questionnaire'}</p>
                              {statusBadge && (
                                <Badge variant="outline" className={statusBadge.className}>
                                  {statusBadge.label}
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {scoreLabel(review.score_percentage, review.passed, review.status)}
                              {review.expires_at && ` · Expires: ${format(new Date(review.expires_at), 'MMM d, yyyy')}`}
                              {review.renewal_status === 'due_soon' && ' · ⚠️ Renewal due soon'}
                              {review.renewal_status === 'overdue' && ' · 🔴 Overdue'}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Progress value={review.completion_percentage} className="w-20" />
                            <Button size="sm" variant="outline" onClick={() => navigate(`/onboarding/review/${review.id}`, { state: { returnTo: '/onboarding?tab=reviews' } })}>
                              <Eye className="h-3.5 w-3.5 mr-1" />Review
                            </Button>
                          </div>
                        </div>
                        );
                      })}

                      {/* CR-5984 Path B: SupplierQualification rows render with
                          the same shape as FilledDataset reviews, but click
                          through to the legacy /intelligence/qualifications/:id
                          review screen where the customer can approve / reject /
                          request-more-info on those submissions. */}
                      {supplier.qualifications.map((qual) => {
                        const statusLabelMap: Record<string, { label: string; className: string }> = {
                          submitted: { label: 'Awaiting Review', className: 'bg-blue-100 text-blue-800' },
                          under_review: { label: 'Under Review', className: 'bg-purple-100 text-purple-800' },
                          more_info_required: { label: 'More Info Requested', className: 'bg-amber-100 text-amber-800' },
                          rejected: { label: 'Rejected — Awaiting Resubmit', className: 'bg-red-100 text-red-800' },
                          approved: { label: 'Approved', className: 'bg-green-100 text-green-800' },
                        };
                        const statusBadge = qual.status ? statusLabelMap[qual.status] : undefined;
                        return (
                          <div key={`qual-${qual.id}`} className="flex items-center gap-4 p-3 bg-white rounded-md border">
                            <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-sm font-medium">{qual.template_name || 'Qualification'}</p>
                                {statusBadge && (
                                  <Badge variant="outline" className={statusBadge.className}>
                                    {statusBadge.label}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {scoreLabel(qual.score_percentage, qual.passed, qual.status)}
                                {qual.expiry_date && ` · Expires: ${format(new Date(qual.expiry_date), 'MMM d, yyyy')}`}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {qual.completion_percentage != null && (
                                <Progress value={qual.completion_percentage} className="w-20" />
                              )}
                              <Button size="sm" variant="outline" onClick={() => navigate(`/intelligence/qualifications/${qual.id}`, { state: { returnTo: '/onboarding?tab=reviews' } })}>
                                <Eye className="h-3.5 w-3.5 mr-1" />Review
                              </Button>
                            </div>
                          </div>
                        );
                      })}

                      {/* Questionnaires already approved for this supplier.
                          Read-only, and shown last so the outstanding work
                          stays at the top of the card. This is the only route
                          back to the answers once a questionnaire is settled. */}
                      {settledForSupplier.map((qual) => (
                        <div key={`settled-${qual.id}`} className="flex items-center gap-4 p-3 bg-muted/30 rounded-md border">
                          <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm font-medium">{qual.template_name || 'Qualification'}</p>
                              <Badge variant="outline" className="bg-green-100 text-green-800">Approved</Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {scoreLabel(qual.score_percentage, qual.passed, qual.status)}
                              {qual.expiry_date && ` · Expires: ${format(new Date(qual.expiry_date), 'MMM d, yyyy')}`}
                            </p>
                          </div>
                          <Button size="sm" variant="outline" className="shrink-0" onClick={() => navigate(`/intelligence/qualifications/${qual.id}`, { state: { returnTo: '/onboarding?tab=reviews' } })}>
                            <Eye className="h-3.5 w-3.5 mr-1" />View Answers
                          </Button>
                        </div>
                      ))}

                      {/* Approve/Reject supplier actions */}
                      {(() => {
                        // CR-5984: orphan workflows have no questionnaire-derived
                        // workflow id, so fall back to the workflow on the orphan
                        // marker. Approve stays disabled until at least one
                        // questionnaire has been reviewed.
                        const workflowId = supplier.reviews.find(r => r.onboarding)?.onboarding ?? supplier.orphanWorkflowId;
                        const allApproved = !isOrphan
                          && (supplier.reviews.length + supplier.qualifications.length) > 0
                          && supplier.reviews.every(r => r.status === 'approved')
                          && supplier.qualifications.every(q => q.status === 'approved');
                        // `allApproved` requires the arrays to be non-empty, but they
                        // hold only OUTSTANDING items — approve the last questionnaire
                        // and they empty, so the condition can never be met in the one
                        // state it exists to describe. The card said "Ready to Approve
                        // Supplier" above a disabled button. `isSettled` is that state
                        // seen from the other side: nothing outstanding, something
                        // reviewed.
                        const canApproveSupplier = !!workflowId && (allApproved || isSettled);
                        // CR-4141 rework 2: roles without approval rights get a
                        // read-only card — no finalise action, and helper text
                        // that doesn't instruct them to do something they can't.
                        // CR-6513: awaiting-submission had no branch here, so it
                        // fell through to "review and approve each questionnaire
                        // above" while the card above showed nothing to review —
                        // one of the contradictory messages the ticket is about.
                        const helperText = !canApproveSuppliers
                          ? 'Your role does not have permission to approve suppliers. Track progress here — an approver will finalise this supplier.'
                          : isOrphan
                            ? 'No questionnaires assigned yet — assign one below to start due diligence.'
                            : isAwaitingSubmission
                              ? (isInProgress
                                  ? 'The supplier has started their questionnaires. Nothing to review until they submit.'
                                  : 'Questionnaires assigned — waiting for the supplier to submit them for review.')
                            : (allApproved || isSettled)
                              ? 'All questionnaires approved. Finalise the supplier below.'
                              : 'Review and approve each questionnaire above — then finalise the supplier.';
                        const approveTooltip = isOrphan
                          ? 'Assign at least one questionnaire before approving'
                          : isAwaitingSubmission
                            ? 'Waiting for the supplier to submit their questionnaire(s)'
                            : canApproveSupplier ? undefined : 'Approve all questionnaires first';
                        return (
                          <>
                          {assignForWorkflow === workflowId && (
                            <div className="mt-3 rounded-md border bg-muted/30 p-3">
                              <p className="text-sm font-medium mb-2">Assign questionnaires to {supplier.name}</p>
                              <div className="space-y-2 max-h-56 overflow-y-auto">
                                {assignableDatasets.map(d => (
                                  <label key={d.id} className="flex items-center gap-2 text-sm cursor-pointer">
                                    <input
                                      type="checkbox"
                                      className="h-4 w-4"
                                      checked={assignPicks.includes(d.id)}
                                      onChange={(e) => setAssignPicks(prev =>
                                        e.target.checked ? [...prev, d.id] : prev.filter(x => x !== d.id)
                                      )}
                                    />
                                    <span>{d.name}{typeof d.question_count === 'number' ? ` — ${d.question_count} questions` : ''}</span>
                                  </label>
                                ))}
                              </div>
                              <div className="flex justify-end gap-2 mt-3">
                                <Button variant="outline" size="sm" onClick={() => { setAssignForWorkflow(null); setAssignPicks([]); }}>Cancel</Button>
                                <Button size="sm" disabled={assignPicks.length === 0 || assigning} onClick={submitAssignment}>
                                  {assigning ? 'Assigning…' : `Assign ${assignPicks.length || ''}`.trim()}
                                </Button>
                              </div>
                            </div>
                          )}
                          <div className="flex items-center justify-between pt-3 border-t gap-3 flex-wrap">
                            <p className="text-xs text-muted-foreground">{helperText}</p>
                            <div className="flex gap-2">
                              {supplier.supplierId && (
                                <Button variant="outline" size="sm" onClick={() => navigate(`/suppliers/wizard/${supplier.supplierId}?step=1`)}>
                                  View Full Profile
                                </Button>
                              )}
                              {/* The way out of "Awaiting Questionnaire
                                  Assignment". Without it the only advice was to
                                  re-invite, which this stage offers no control
                                  for, so the supplier could never be finished. */}
                              {canApproveSuppliers && isOrphan && workflowId && (
                                <Button
                                  size="sm"
                                  onClick={() => { setAssignForWorkflow(workflowId); setAssignPicks([]); }}
                                  disabled={assignableDatasets.length === 0}
                                  title={assignableDatasets.length === 0 ? 'No active questionnaires exist yet — create one on the Onboarding Datasets page' : undefined}
                                >
                                  <ClipboardList className="h-4 w-4 mr-2" />Assign Questionnaire
                                </Button>
                              )}
                              {canApproveSuppliers && (
                              <Button
                                size="sm"
                                disabled={!canApproveSupplier || approvingWorkflow === workflowId}
                                title={approveTooltip}
                                onClick={async () => {
                                  if (!workflowId) return;
                                  setApprovingWorkflow(workflowId);
                                  try {
                                    await onboardingAPI.approveWorkflow(workflowId);
                                    toast.success(`${supplier.name} approved.`);
                                    onAction?.();
                                  } catch (err: unknown) {
                                    console.error('Failed to approve supplier:', err);
                                    const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Failed to approve supplier';
                                    toast.error(msg);
                                  } finally {
                                    setApprovingWorkflow(null);
                                  }
                                }}
                              >
                                {approvingWorkflow === workflowId ? 'Approving…' : 'Approve Supplier'}
                              </Button>
                              )}
                            </div>
                          </div>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
